import { revalidatePath } from 'next/cache';
import { apiUrl } from '@/lib/site';

/**
 * Clear the rendered-page cache, on demand.
 *
 * Pages are cached for 300s, so a story published by the pipeline can take
 * up to five minutes to appear. That is the right default for readers and
 * the wrong one for an operator who has just clicked "Fetch news now" and
 * wants to see the result.
 *
 * This has to live in the web app rather than the API: the cache belongs
 * to the Next.js process, and nothing the NestJS process does can reach
 * it.
 *
 * Authorisation is delegated to the API rather than reimplemented here.
 * The session is an HMAC-signed token, and a second implementation of
 * that check is a second thing to get wrong — one that fails open would
 * expose the endpoint to anyone. Asking the API "is this cookie valid?"
 * has exactly one source of truth.
 */
export async function POST(request: Request): Promise<Response> {
  const cookie = request.headers.get('cookie') ?? '';

  // No cookie at all: reject without troubling the API.
  if (!cookie.includes('ai_radar_admin=')) {
    return Response.json({ ok: false, error: 'Not signed in' }, { status: 401 });
  }

  try {
    const check = await fetch(`${apiUrl()}/admin/session`, {
      headers: { cookie },
      cache: 'no-store',
      signal: AbortSignal.timeout(10_000),
    });

    if (!check.ok) {
      return Response.json({ ok: false, error: 'Not signed in' }, { status: 401 });
    }
  } catch {
    // The API being unreachable must not become an open door.
    return Response.json({ ok: false, error: 'Could not verify session' }, { status: 503 });
  }

  // 'layout' clears the whole subtree beneath each path, so every locale,
  // category and story page goes at once rather than only the exact URL.
  const paths = ['/', '/en', '/ar'];
  for (const path of paths) {
    revalidatePath(path, 'layout');
  }

  return Response.json({ ok: true, cleared: paths.length });
}
